import React, { useState, useEffect } from 'react';
import { Box, Button, Grid, Paper, Typography, useMediaQuery, useTheme, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useGetExamQuestions } from './properties/useGetExamQuestions';
import TextQuestion from './components/TextQuestions';
import MultipleChoiceQuestion from './components/MultipleChoiceQuestion';
import MultiSelectQuestion from './components/MultiSelectQuestion';
import LikertComponent from './components/LikertComponent';
import { useGetAssessment } from './properties/useGetAssessment';
import { FetchStatus, IEntity } from '../../../base/types';
import { IFieldValue } from '../../../services/models';
import { useAuth } from '../../../../modules/auth';
import { useCreateMutation, useUpdateMutation } from '../../../services/data/tableDataApi';
import { useDataContext } from '../../page/context';
import { useParams, useNavigate } from 'react-router-dom';
import { ProgressIndicator } from '../../../widgets';
import { useGetExamMarks } from './properties/useGetExamMarks';
import { useGetCountDown } from './components/useGetCountDown';
import { QuestionState } from './properties/assessment';
import { GeneralAwareness } from './components/GeneralAwareness';
import { FaCalculator } from 'react-icons/fa';
interface FormData {
  [key: string]: string | string[];
}

const initialValues: FormData = {};

export const ExamQuestionsComponent = () => {
  const { examId } = useParams<{ examId?: string }>();
  const { questions } = useGetExamQuestions(examId || '');
  const { examMarksId } = useGetExamMarks(examId || '');
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(0);
  const [questionStates, setQuestionStates] = useState<QuestionState[]>([]);
  const { assessments } = useGetAssessment();
  const exam = assessments.find(assessment => assessment.assessmentId === examId);
  const [filteredQuestions, setFilteredQuestions] = useState(questions);
  const { user, siteId } = useAuth();
  const navigate = useNavigate();
  const [showSubmissionScreen, setShowSubmissionScreen] = useState(false); // State to control submission screen
  const [modifyRecord] = useUpdateMutation();
  const [insertRecord] = useCreateMutation();
  const { setData } = useDataContext();
  const [formData, setFormData] = useState<FormData>(initialValues);
  const [loading, setLoading] = useState<boolean>(false);
  const { getFormattedTime, remainingTime } = useGetCountDown(exam)
  const [fullscreenOpen, setFullscreenOpen] = useState(false);
  const [copyPasteOpen, setCopyPasteOpen] = useState(false);
  const [tabSwitchOpen, setTabSwitchOpen] = useState(false);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

  const handleFullscreenClose = () => {
    setFullscreenOpen(false);
    enterFullScreen();
  };

  const enterFullScreen = () => {
    const element = document.documentElement;
    if (element.requestFullscreen) {
      element.requestFullscreen();
    } else if ((element as any).webkitRequestFullscreen) {
      (element as any).webkitRequestFullscreen();
    } else if ((element as any).msRequestFullscreen) {
      (element as any).msRequestFullscreen();
    }
  };

  const handleFullScreenChange = () => {
    if (!document.fullscreenElement) {
      setFullscreenOpen(true);
    }
  };

  const handleCopyPaste = (e: ClipboardEvent) => {
    e.preventDefault();
    setCopyPasteOpen(true);
  };

  const handleCopyPasteClose = () => {
    setCopyPasteOpen(false);
  };

  const handleTabSwitch = () => {
    setTabSwitchOpen(true);
  };

  const handleTabSwitchClose = () => {
    setTabSwitchOpen(false);
    window.focus();
  };

  useEffect(() => {
    enterFullScreen();
    document.addEventListener('fullscreenchange', handleFullScreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullScreenChange);
    document.addEventListener('mozfullscreenchange', handleFullScreenChange);
    document.addEventListener('MSFullscreenChange', handleFullScreenChange);
    document.addEventListener('copy', handleCopyPaste);
    document.addEventListener('cut', handleCopyPaste);
    document.addEventListener('paste', handleCopyPaste);
    window.addEventListener('blur', handleTabSwitch);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullScreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullScreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullScreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullScreenChange);
      document.removeEventListener('copy', handleCopyPaste);
      document.removeEventListener('cut', handleCopyPaste);
      document.removeEventListener('paste', handleCopyPaste);
      window.removeEventListener('blur', handleTabSwitch);
    };
  }, []);
  const [calculatorOpen, setCalculatorOpen] = useState(false);

  const handleCalculatorOpen = () => {
    setCalculatorOpen(true);
  };

  const handleCalculatorClose = () => {
    setCalculatorOpen(false);
  };
  const updateQuestionState = (index: number, updates: Partial<QuestionState>) => {
    setQuestionStates(prevStates =>
      prevStates.map((state, i) =>
        i === index ? { ...state, ...updates } : state
      )
    );
  };

  const handleQuestionVisit = (index: number) => {
    const currentState = questionStates[index];
    if (!currentState.visited) {
      updateQuestionState(index, { visited: true });
    }
  };

  const handleClearResponse = () => {
    const currentIndex = currentQuestionIndex;
    updateQuestionState(currentIndex, { selectedOption: null, markedForReview: false });
    setFormData(prevData => ({ ...prevData, [currentQuestion.questionId]: '' }));
  };

  const handleMarkForReview = () => {
    updateQuestionState(currentQuestionIndex, { markedForReview: true });
    handleNextQuestion();
  };

  const handleAnswerChange = (questionId: string, answer: string) => {
    const index = filteredQuestions.findIndex(q => q.questionId === questionId);
    const state = questionStates[index];
    const wasMarkedForReview = state?.markedForReview;
    const isAnswered = answer !== '';
    const wasAnswered = state?.selectedOption !== null;

    updateQuestionState(index, {
      selectedOption: answer,
      markedForReview: wasMarkedForReview && !isAnswered ? true : false
    });

    if (wasAnswered && !wasMarkedForReview && isAnswered) {
    } else if (isAnswered) {
      // Question answered
      updateQuestionState(index, { markedForReview: false });
    }
  };

  useEffect(() => {
    if (questions) {
      const filtered = questions.filter(question => question.questionType);
      setFilteredQuestions(filtered);
      const initialData: FormData = {};
      filtered.forEach(question => {
        initialData[question.questionId] = question.questionType === 365970000 && question.mcqType === 365970000 ? [] : '';
      });
      setFormData(initialData);
    }
    // eslint-disable-next-line
  }, [questions]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    handleAnswerChange(name, value);
  };
  const handleLikertChange = (value: any) => {
    console.log("value here", value);
  }

  const handleMultiSelectChange = (name: string, selectedValues: string[]) => {
    setFormData({
      ...formData,
      [name]: selectedValues,
    });
    handleAnswerChange(name, selectedValues.join(','));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const responsePromises = filteredQuestions
        .filter(question => formData[question.questionId]?.length > 0)
        .map(async question => {
          const responseValue = formData[question.questionId];
          if (question.questionType === 365970001) {
            return saveDescriptiveResponse(question.questionId, responseValue as string);
          } else if (question.mcqType === 365970000) {
            return saveMultiSelectResponse(question.questionId, responseValue as string[]);
          } else if (question.mcqType === 365970001) {
            return saveMultipleChoiceResponse(question.questionId, responseValue as string);
          } else {
            return null;
          }
        });

      const responses = await Promise.all(responsePromises);
      setData({ type: "Card", result: responses, status: FetchStatus.Success });
      setShowSubmissionScreen(true);
      setTimeout(() => {
        window.location.href =
          "https://edtech365-pfx.azurewebsites.net/site(4d100008-7828-ef11-840a-000d3af278af)/page(da5285e0-15c4-11f0-a306-7fb3ae668eef)";
      }, 5000);
    }
    catch (error) {
      console.error("Submission Error:", error);
    }
  };

  const saveMultiSelectResponse = async (questionId: string, selectedValues: string[]): Promise<IEntity[]> => {
    setLoading(true);
    const examMarkRecord = examMarksId.find(mark => mark.questionMappingId === questionId);
    if (examMarkRecord) {
      const insertPromises = selectedValues.map(selectedValue => {
        const fields: IFieldValue[] = [
          { field: "edtec365_choice", value: selectedValue, validate: true },
          { field: "edtec365_exammarks", value: examMarkRecord.examMarksId, validate: true },
        ];

        return insertRecord({
          portalId: siteId || '',
          userId: user?.contactId,
          tableId: "edtec365_choiceresponse",
          data: fields,
          triggers: true,
        })
          .unwrap()
          .catch(err => {
            const message = err.data?.message || 'An error occurred while saving the multi-select response. Please try again later.';
            console.error("Multi-Select Response Error:", message);
            return Promise.reject(message);
          });
      });
      const responseRecords = await Promise.all(insertPromises);
      setLoading(false);
      return responseRecords;
    } else {
      console.warn(`No exam mark found for questionId: ${questionId}`);
      setLoading(false);
      return Promise.resolve([] as IEntity[]);
    }
  };

  const saveMultipleChoiceResponse = (questionId: string, selectedValue: string): Promise<IEntity> => {
    setLoading(true);
    const examMarkRecord = examMarksId.find(mark => mark.questionMappingId === questionId);
    if (examMarkRecord) {
      const fields: IFieldValue[] = [
        { field: "edtec365_choice", value: selectedValue, validate: true },
        { field: "edtec365_exammarks", value: examMarkRecord.examMarksId, validate: true },
      ];

      return insertRecord({
        portalId: siteId || '',
        userId: user?.contactId,
        tableId: "edtec365_choiceresponse",
        data: fields,
        triggers: true,
      })
        .unwrap()
        .catch(err => {
          const message = err.data?.message || 'An error occurred while saving the multiple-choice response. Please try again later.';
          console.error("Multiple Choice Response Error:", message);
          return Promise.reject(message);
        })
        .finally(() => setLoading(false));
    } else {
      console.warn(`No exam mark found for questionId: ${questionId}`);
      return Promise.resolve({} as IEntity);
    }
  };

  const saveDescriptiveResponse = (questionId: string, responseValue: string): Promise<IEntity> => {
    setLoading(true);
    const examMarkRecord = examMarksId.find(mark => mark.questionMappingId === questionId);
    if (examMarkRecord) {
      const fields: IFieldValue[] = [
        { field: "edtec365_questionresponse", value: responseValue, validate: true },
      ];

      return modifyRecord({
        portalId: siteId || '',
        userId: user?.contactId,
        tableId: "slcm_exammarks",
        data: fields,
        sysId: examMarkRecord.examMarksId,
        triggers: true,
      })
        .unwrap()
        .catch(err => {
          const message = err.data?.message || 'An error occurred while saving the descriptive response. Please try again later.';
          console.error("Descriptive Response Error:", message);
          return Promise.reject(message);
        })
        .finally(() => setLoading(false));
    } else {
      console.warn(`No exam mark found for questionId: ${questionId}`);
      return Promise.resolve({} as IEntity);
    }
  };

  useEffect(() => {
    if (filteredQuestions.length > 0) {
      setQuestionStates(filteredQuestions.map(() => ({
        visited: false,
        markedForReview: false,
        selectedOption: null,
      })));
    }
    // eslint-disable-next-line
  }, [filteredQuestions]);

  useEffect(() => {
    if (filteredQuestions.length > 0) {
      const updatedStates = filteredQuestions.map((_, index) => ({
        visited: questionStates[index]?.visited || false,
        markedForReview: questionStates[index]?.markedForReview || false,
        selectedOption: formData[filteredQuestions[index].questionId] || null,
      }));
      setQuestionStates(updatedStates);
    }
    // eslint-disable-next-line
  }, [formData, filteredQuestions]);

  const handleNextQuestion = () => {
    setQuestionStates(prevStates => {
      const updatedStates = [...prevStates];
      if (updatedStates[currentQuestionIndex]) {
        updatedStates[currentQuestionIndex] = {
          ...updatedStates[currentQuestionIndex],
          visited: true,
        };
      }
      return updatedStates;
    });
    setCurrentQuestionIndex(prevIndex => (prevIndex < filteredQuestions.length - 1 ? prevIndex + 1 : prevIndex));
  };

  const getColorForQuestion = (index: number) => {
    const questionState = questionStates[index];
    if (!questionState) return '#2196f3';
    if (questionState.selectedOption && questionState.markedForReview) return '#8a2be2';
    if (questionState.selectedOption) return '#4caf50';
    if (questionState.markedForReview) return '#ffeb3b';
    if (questionState.visited) return '#f44336';
    return '#2196f3';
  };

  const currentQuestion = filteredQuestions[currentQuestionIndex];

  return (<>
    <Box sx={{ padding: { xs: 2, md: 3 } }}>
      {showSubmissionScreen && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(5px)',
            zIndex: 1000,
          }}
        />
      )}
      <form onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box sx={{ backgroundColor: '#007bff', padding: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" color='white'>
                {exam?.name}
              </Typography>
              <Box textAlign="right" display="flex" alignItems="center">
                <FaCalculator color="white" size={24}
                  style={{ cursor: 'pointer', marginRight: 12 }}
                  fontSize="medium"
                  onClick={handleCalculatorOpen} 
                 />
                <AccessTimeIcon sx={{ color: 'white', marginRight: 0.3 }} />
                <Typography
                  variant="h5"
                  sx={{
                    marginTop: 0,
                    fontWeight: 'bolder',
                    marginLeft: 0,
                    color: remainingTime <= 120 ? 'red' : 'yellow',
                  }}
                >
                  {getFormattedTime(remainingTime)}
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} md={9}>
            {(currentQuestion && filteredQuestions) && (
              <Paper sx={{ padding: 3, minHeight: '350px', overflowY: 'auto' }}>
                <Typography variant="h6">Question No. {currentQuestion.questionNumber}</Typography>
                <Typography variant="body1" sx={{ marginTop: 2 }}>
                  <label className="form-label">
                    <span dangerouslySetInnerHTML={{ __html: currentQuestion.questionDescription }}></span>
                  </label>
                </Typography>

                {currentQuestion.questionType === 365970001 && (
                  <TextQuestion
                    question={currentQuestion}
                    name={currentQuestion.questionId}
                    value={formData[currentQuestion.questionId] as string}
                    onChange={handleChange}
                    onBlur={() => handleQuestionVisit(currentQuestionIndex)}
                  />
                )}
                {currentQuestion.questionType === 365970004 && (
                  <LikertComponent
                    question={currentQuestion}
                    name={currentQuestion.questionId}
                    value={formData[currentQuestion.questionId] as string}
                    onChange={handleLikertChange}
                    onBlur={() => handleQuestionVisit(currentQuestionIndex)}
                  />
                )}

                {(currentQuestion.questionType === 365970000 && currentQuestion.mcqType === 365970001) && (
                  <MultipleChoiceQuestion
                    question={currentQuestion}
                    name={currentQuestion.questionId}
                    value={formData[currentQuestion.questionId] as string}
                    onChange={handleChange}
                    onBlur={() => handleQuestionVisit(currentQuestionIndex)}
                  />
                )}

                {(currentQuestion.questionType === 365970000 && currentQuestion.mcqType === 365970000) && (
                  <MultiSelectQuestion
                    question={currentQuestion}
                    value={formData[currentQuestion.questionId] as string[]}
                    onChange={(selectedValues) => handleMultiSelectChange(currentQuestion.questionId, selectedValues)}
                    onBlur={() => handleQuestionVisit(currentQuestionIndex)}
                  />
                )}
              </Paper>
            )}

            <Box sx={{ marginTop: 2, display: 'flex', justifyContent: 'space-between', gap: 2 }}>
              <Button disabled={currentQuestionIndex === filteredQuestions.length - 1} variant="outlined" onClick={handleMarkForReview}>Mark for Review & Next</Button>
              <Button variant="outlined" onClick={handleClearResponse}>Clear Response</Button>
              <Button disabled={currentQuestionIndex === filteredQuestions.length - 1} variant="contained" color="primary" onClick={handleNextQuestion}>Save & Next</Button>
            </Box>
          </Grid>

          <Grid item xs={12} md={3}>
            <Grid item xs={12} mb={1}>
              <GeneralAwareness questions={filteredQuestions} questionStates={questionStates} />
            </Grid>
            <Box sx={{ textAlign: 'center', marginBottom: 1, marginTop: 1.5 }}>
              <Typography variant="h6" sx={{ marginBottom: 0.5 }}>
                Choose a Question
              </Typography>
              <Typography variant="subtitle2">
                Select a question to navigate
              </Typography>
            </Box>
            <Paper sx={{ padding: 3, maxHeight: 215, overflowY: 'auto' }}>
              <Grid container spacing={1}>
                {filteredQuestions.map((question, index) => (
                  <Grid item xs={4} sm={3} key={question.questionId}>
                    <Box
                      sx={{
                        backgroundColor: getColorForQuestion(index),
                        width: '100%',
                        height: 40,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: 1,
                        color: '#fff',
                        textAlign: 'center'
                      }}
                    >
                      <Button
                        variant="text"
                        sx={{ color: '#fff', width: '100%', height: '100%' }}
                        onClick={() => setCurrentQuestionIndex(index)}
                      >
                        {index + 1}
                      </Button>
                    </Box>
                  </Grid>
                ))}
              </Grid>

            </Paper>
            <Button disabled={currentQuestionIndex !== filteredQuestions.length - 1} type='submit' variant="contained" color="success" fullWidth sx={{ marginTop: 2 }}>
              {loading ? (
                <ProgressIndicator show={true} message='Please wait...' />
              ) : (
                'Submit'
              )}
            </Button>
          </Grid>
        </Grid>
      </form>
      {showSubmissionScreen && (
        <Dialog open={showSubmissionScreen}>
          <DialogTitle>Submission Successful</DialogTitle>
          <DialogContent>
            <Typography variant="h6" color="green">Your Exam Responses are saved successfully</Typography>
          </DialogContent>
          <DialogActions>
            <Button disabled>Redirecting to feedback...</Button>
          </DialogActions>
        </Dialog>
      )}
      <Dialog
        fullScreen={fullScreen}
        open={fullscreenOpen}
        onClose={handleFullscreenClose}
        aria-labelledby="fullscreen-warning-dialog-title"
      >
        <DialogTitle id="fullscreen-warning-dialog-title">
          <h4 className="text-danger">Warning</h4>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            You cannot exit fullscreen during the exam. Please stay in full screen mode.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button autoFocus onClick={handleFullscreenClose}>
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        fullScreen={fullScreen}
        open={copyPasteOpen}
        onClose={handleCopyPasteClose}
        aria-labelledby="copy-paste-warning-dialog-title"
      >
        <DialogTitle id="copy-paste-warning-dialog-title">
          <h4 className="text-danger">Warning</h4>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Copying, Cutting, or Pasting content is not allowed during the exam.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button autoFocus onClick={handleCopyPasteClose}>
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        fullScreen={fullScreen}
        open={tabSwitchOpen}
        onClose={handleTabSwitchClose}
        aria-labelledby="tab-switch-warning-dialog-title"
      >
        <DialogTitle id="tab-switch-warning-dialog-title">
          <h4 className="text-danger">Warning</h4>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            You cannot switch tabs during the exam. Please stay on this page.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button autoFocus onClick={handleTabSwitchClose}>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
    <Dialog open={calculatorOpen} onClose={handleCalculatorClose} fullWidth maxWidth="sm">
      <DialogTitle>Scientific Calculator</DialogTitle>
      <DialogContent>
        {/* You can replace below with your custom calculator or another package */}
        <iframe
          src="https://www.desmos.com/scientific"
          title="Scientific Calculator"
          style={{ border: 'none', width: '100%', height: '500px' }}
        />
      </DialogContent>
    </Dialog>  </>
  );
};
